import { useEffect, useState, useCallback } from "react";
import { ClipboardText as ClipboardCheck, CheckCircle, Warning, XCircle, ArrowsClockwise, FileText, Crosshair } from "@phosphor-icons/react";
import { http, API } from "@/lib/api";

const dvirApi = {
  list: () => http.get("/dvir").then((r) => r.data),
  summary: () => http.get("/dvir/summary").then((r) => r.data),
};

const STATUS = {
  passed:           { color: "#22c55e", icon: CheckCircle, label: "PASSED" },
  defects:          { color: "#ff8c00", icon: Warning, label: "DEFECTS" },
  out_of_service:   { color: "#ef4444", icon: XCircle, label: "OUT OF SERVICE" },
};

export default function DVIRPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, today_total: 0, today_defects: 0, today_oos: 0, passed_pct: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([dvirApi.list(), dvirApi.summary()]);
      setRows(r); setSummary(s);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="dvir-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">FMCSA 49 CFR § 396.11</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Driver Vehicle Inspection Reports</h1>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.25em] font-bold flex items-center gap-2" data-testid="dvir-refresh">
          <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Tile label="Total DVIRs" value={summary.total} accent="#a1a1aa" testid="dvir-tile-total" />
        <Tile label="Today" value={summary.today_total} accent="#4682b4" testid="dvir-tile-today" />
        <Tile label="Today · Defects" value={summary.today_defects} accent="#ff8c00" testid="dvir-tile-defects" />
        <Tile label="Today · OOS" value={summary.today_oos} accent="#ef4444" testid="dvir-tile-oos" />
        <Tile label="Pass rate" value={`${summary.passed_pct}%`} accent="#22c55e" testid="dvir-tile-pass" />
      </div>

      <div className="bg-[#0d0d0d] border border-[#2a2a2a]" data-testid="dvir-list">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Recent Inspections</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{rows.length} showing</span>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-14 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]">
            No inspections yet — drivers submit pre/post-trip DVIRs from the driver PWA.
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {rows.map((r) => {
              const s = STATUS[r.status] || STATUS.passed;
              const Icon = s.icon;
              return (
                <div key={r.id} className="px-4 py-3 flex items-start gap-3 flex-wrap" data-testid={`dvir-row-${r.id}`}>
                  <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: s.color, color: "#000" }} title={s.label}>
                    <Icon size={14} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-[#ff8c00] text-sm">{r.asset_name || r.asset_id.slice(0, 8)}</span>
                      <span className="text-[9px] uppercase tracking-[0.25em] font-black px-1.5 py-0.5" style={{ background: s.color, color: "#000" }}>{s.label}</span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa]">{(r.inspection_type || "").replace("_", "-")}</span>
                    </div>
                    <div className="text-[11px] text-[#a1a1aa] mt-0.5">
                      Signed by <strong className="text-white">{r.signature_name}</strong> · {r.driver_email}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{new Date(r.created_at).toLocaleString()}</span>
                      {r.odometer_miles ? <span>· {r.odometer_miles.toLocaleString()} mi</span> : null}
                      {r.lat != null && (
                        <a href={`https://maps.google.com/?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer" className="text-[#22c55e] hover:underline flex items-center gap-1" data-testid={`dvir-gps-${r.id}`}>
                          <Crosshair size={9} weight="bold" /> {r.lat.toFixed(4)}, {r.lng.toFixed(4)}
                        </a>
                      )}
                    </div>
                    {(r.defects || []).length > 0 && (
                      <ul className="mt-1 text-[11px] text-[#a1a1aa] space-y-0.5">
                        {r.defects.slice(0, 4).map((d, i) => (
                          <li key={i}>
                            <span className="text-[#ef4444] font-mono">▸</span> <strong className="text-white">{(d.item || "").replace(/_/g, " ")}</strong>
                            <span className="text-[10px] ml-1" style={{ color: d.severity === "out_of_service" ? "#ef4444" : d.severity === "major" ? "#ff8c00" : "#71717a" }}>
                              ({(d.severity || "").toUpperCase()})
                            </span>
                            {d.note ? <span className="text-[#a1a1aa]"> — {d.note}</span> : null}
                          </li>
                        ))}
                        {r.defects.length > 4 && <li className="text-[#71717a]">+ {r.defects.length - 4} more…</li>}
                      </ul>
                    )}
                  </div>
                  <a href={`${API}/dvir/${r.id}/pdf`} target="_blank" rel="noreferrer" className="w-7 h-7 border border-[#2a2a2a] hover:border-[#4682b4] hover:text-[#4682b4] flex items-center justify-center shrink-0" title="View PDF" data-testid={`dvir-pdf-${r.id}`}>
                    <FileText size={11} weight="bold" />
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-heading font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
